/* eslint-disable no-console */
import { useState, useEffect, useMemo, useCallback } from "react";
import { FaTrash, FaUpload, FaCheckCircle } from "react-icons/fa";

import { USERS_ALL_VENTAS } from "../../constants/permisos";
import { ROLES } from "../../constants/roles";
import { useAuthContext } from "../../context/auth";
import {
  getOportunidades,
  getEdificiosFull,
  getFotosBySaleId,
  getReportesBySaleIds,
  deleteFotoReporte,
} from "../../utils";
import { Header } from "../Header";
import SubirFotoReporteModal from "./SubirFotoReporteModal";

const ROLES_EDITOR = [ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.COMERCIAL, ROLES.ADMINISTRATIVO];

// Karen (306) y Nicolás (904) acceden a todas las ventas para crear reportes
// USERS_ALL_VENTAS importado de permisos.js

const ReporteFotografico = () => {
  const { pais, id: userId, rol } = useAuthContext();
  const canEdit = ROLES_EDITOR.includes(rol);

  const [ventas, setVentas] = useState([]);
  const [edificiosMap, setEdificiosMap] = useState({});
  const [selectedSaleId, setSelectedSaleId] = useState("");
  const [fotos, setFotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingFotos, setLoadingFotos] = useState(false);

  // Reportes iniciados (ventas que ya tienen al menos 1 foto)
  const [ventasConReporte, setVentasConReporte] = useState([]);

  // Modal subir foto
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadEdificio, setUploadEdificio] = useState(null);

  // Cargar ventas y edificios
  useEffect(() => {
    if (!pais || pais === "Region") return;
    const currentYear = new Date().getFullYear();

    const loadData = async () => {
      try {
        const [salesData, edData] = await Promise.all([
          getOportunidades({ pais, ano: currentYear }).catch(() => []),
          getEdificiosFull(),
        ]);

        const allSales = salesData || [];

        // Filtrar: cerradas (100%) + con edificios asignados
        const ventasFiltradas = allSales
          .filter((s) => s.estatus === "100%" && s.edificiosparticipantes && s.edificiosparticipantes.trim() !== "")
          .sort((a, b) => (b.id || 0) - (a.id || 0));

        // Si es comercial, solo sus ventas (excepto Karen y Nicolás que ven todas)
        const ventasFinales =
          rol === ROLES.COMERCIAL && !USERS_ALL_VENTAS.includes(userId)
            ? ventasFiltradas.filter((s) => String(s.ejecutivo) === String(userId))
            : ventasFiltradas;

        setVentas(ventasFinales);

        if (edData?.length > 0) {
          setEdificiosMap(edData.reduce((acc, e) => ({ ...acc, [e.id]: e }), {}));
        }
      } catch (error) {
        console.error("Error cargando datos:", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [pais, rol, userId]);

  // Cargar qué ventas ya tienen reportes iniciados
  useEffect(() => {
    if (ventas.length === 0) return;
    const loadReportes = async () => {
      try {
        const saleIds = ventas.map((v) => v.id);
        const data = await getReportesBySaleIds(saleIds);
        const idsConFotos = [...new Set(data.map((r) => r.sale_id))];
        setVentasConReporte(idsConFotos);
      } catch (error) {
        console.error("Error cargando reportes iniciados:", error);
      }
    };
    loadReportes();
  }, [ventas]);

  // Cargar fotos cuando se selecciona una venta
  useEffect(() => {
    if (!selectedSaleId) {
      setFotos([]);
      return;
    }
    const loadFotos = async () => {
      setLoadingFotos(true);
      try {
        const data = await getFotosBySaleId(Number(selectedSaleId));
        setFotos(data);
      } catch (error) {
        console.error("Error cargando fotos:", error);
      } finally {
        setLoadingFotos(false);
      }
    };
    loadFotos();
  }, [selectedSaleId]);

  const selectedSale = useMemo(() => ventas.find((v) => v.id === Number(selectedSaleId)), [ventas, selectedSaleId]);

  // Edificios participantes de la venta seleccionada
  const edificiosVenta = useMemo(() => {
    if (!selectedSale?.edificiosparticipantes) return [];
    const ids = selectedSale.edificiosparticipantes
      .split(",")
      .map((s) => Number(s.trim()))
      .filter(Boolean);
    return ids
      .map((id) => edificiosMap[id])
      .filter(Boolean)
      .sort((a, b) => (a.edificio || "").localeCompare(b.edificio || ""));
  }, [selectedSale, edificiosMap]);

  // Fotos agrupadas por edificio
  const fotosPorEdificio = useMemo(() => {
    const map = {};
    fotos.forEach((f) => {
      if (!map[f.idedificio]) map[f.idedificio] = [];
      map[f.idedificio].push(f);
    });
    return map;
  }, [fotos]);

  const edificiosCompletos = useMemo(
    () => edificiosVenta.filter((e) => fotosPorEdificio[e.id]?.length > 0).length,
    [edificiosVenta, fotosPorEdificio]
  );

  const totalFotos = fotos.length;

  const refreshFotos = useCallback(async () => {
    if (!selectedSaleId) return;
    try {
      const data = await getFotosBySaleId(Number(selectedSaleId));
      setFotos(data);
      const saleNum = Number(selectedSaleId);
      if (data.length > 0) {
        setVentasConReporte((prev) => (prev.includes(saleNum) ? prev : [...prev, saleNum]));
      } else {
        setVentasConReporte((prev) => prev.filter((id) => id !== saleNum));
      }
    } catch (error) {
      console.error("Error recargando fotos:", error);
    }
  }, [selectedSaleId]);

  const handleDeleteMedia = async (record) => {
    if (!window.confirm("¿Eliminar este archivo del reporte?")) return;
    try {
      await deleteFotoReporte(record);
      await refreshFotos();
    } catch (error) {
      console.error("Error eliminando archivo:", error);
    }
  };

  const handleOpenUpload = (edificio) => {
    setUploadEdificio(edificio);
    setShowUploadModal(true);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  return (
    <div style={{ padding: 15 }}>
      <Header />

      <div className="container-fluid px-4 py-3">
        {/* Selector de venta */}
        <div className="row mb-4">
          <div className="col-md-6">
            <label className="form-label fw-semibold">Seleccionar venta</label>
            <select className="form-select" value={selectedSaleId} onChange={(e) => setSelectedSaleId(e.target.value)}>
              <option value="">-- Seleccione una venta --</option>
              {ventas
                .filter((v) => !ventasConReporte.includes(v.id))
                .map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.cliente}
                    {v.campana ? ` — ${v.campana}` : ""}
                    {` (${v.edificiostotales} edif.)`}
                  </option>
                ))}
            </select>
          </div>
        </div>

        {/* Reportes iniciados */}
        {!selectedSaleId && ventasConReporte.length > 0 && (
          <div className="mb-4">
            <label className="form-label fw-semibold text-muted" style={{ fontSize: "0.85rem" }}>
              Reportes iniciados
            </label>
            <div className="d-flex flex-wrap gap-2">
              {ventasConReporte.map((saleId) => {
                const venta = ventas.find((v) => v.id === saleId);
                if (!venta) return null;
                return (
                  <button
                    key={saleId}
                    className="btn btn-outline-success btn-sm"
                    style={{ fontSize: "0.8rem" }}
                    onClick={() => setSelectedSaleId(String(saleId))}
                  >
                    <FaCheckCircle className="me-1" style={{ fontSize: "0.7rem" }} />
                    {venta.cliente}
                    {venta.campana ? ` — ${venta.campana}` : ""}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Resumen de la venta */}
        {selectedSale && (
          <>
            <div className="row mb-3">
              <div className="col-12">
                <div className="p-3 bg-light rounded border">
                  <div className="row">
                    <div className="col-md-3">
                      <small className="text-muted">Cliente</small>
                      <div className="fw-semibold">{selectedSale.cliente}</div>
                    </div>
                    {selectedSale.campana && (
                      <div className="col-md-3">
                        <small className="text-muted">Campa&ntilde;a</small>
                        <div className="fw-semibold">{selectedSale.campana}</div>
                      </div>
                    )}
                    {selectedSale.intermediario && (
                      <div className="col-md-2">
                        <small className="text-muted">Agencia</small>
                        <div className="fw-semibold">{selectedSale.intermediario}</div>
                      </div>
                    )}
                    <div className="col-md-2">
                      <small className="text-muted">Edificios</small>
                      <div className="fw-semibold">{selectedSale.edificiostotales}</div>
                    </div>
                    {selectedSale.periodopauta && (
                      <div className="col-md-2">
                        <small className="text-muted">Per&iacute;odo</small>
                        <div className="fw-semibold">{selectedSale.periodopauta} d&iacute;as</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Barra de progreso */}
            <div className="row mb-3">
              <div className="col-12">
                <div className="d-flex align-items-center gap-3">
                  <span className="fw-semibold" style={{ fontSize: "0.9rem" }}>
                    {edificiosCompletos} de {edificiosVenta.length} edificios con contenido
                  </span>
                  <span className="text-muted" style={{ fontSize: "0.85rem" }}>
                    ({totalFotos} {totalFotos === 1 ? "archivo" : "archivos"} en total)
                  </span>
                  <div className="flex-grow-1">
                    <div className="progress" style={{ height: "8px" }}>
                      <div
                        className="progress-bar bg-success"
                        style={{
                          width:
                            edificiosVenta.length > 0 ? `${(edificiosCompletos / edificiosVenta.length) * 100}%` : "0%",
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Grilla compacta de edificios */}
            {loadingFotos ? (
              <div className="text-center py-4">
                <div className="spinner-border spinner-border-sm text-primary" role="status" />
                <span className="ms-2">Cargando fotos...</span>
              </div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                  gap: 12,
                  padding: "16px 24px",
                }}
              >
                {edificiosVenta.map((edificio) => {
                  const mediaEdificio = fotosPorEdificio[edificio.id] || [];
                  const foto = mediaEdificio.find((m) => m.tipo === "foto") || null;
                  const video = mediaEdificio.find((m) => m.tipo === "video") || null;
                  const completo = mediaEdificio.length > 0;
                  const tieneSlotLibre = !foto || !video;
                  const itemHeight = foto && video ? 80 : 120;

                  const deleteBtnStyle = {
                    top: 4,
                    right: 4,
                    padding: "2px 4px",
                    fontSize: "0.55rem",
                    background: "rgba(220,53,69,0.85)",
                    color: "#fff",
                    borderRadius: "50%",
                    border: "none",
                    lineHeight: 1,
                  };

                  return (
                    <div
                      key={edificio.id}
                      style={{
                        border: completo ? "1.5px solid #198754" : "1px solid #dee2e6",
                        borderRadius: 6,
                        background: completo ? "#f0fdf4" : "#fff",
                        cursor: canEdit && tieneSlotLibre ? "pointer" : "default",
                        fontSize: "0.7rem",
                        position: "relative",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                      }}
                      onClick={() => {
                        if (canEdit && tieneSlotLibre) handleOpenUpload(edificio);
                      }}
                      title={edificio.direccion || edificio.ubicacion || edificio.edificio}
                    >
                      {/* Título y dirección arriba, ancho completo */}
                      <div style={{ padding: "6px 8px", lineHeight: 1.3 }}>
                        <div className="d-flex align-items-center gap-1" style={{ marginBottom: 2 }}>
                          <span
                            className="fw-semibold"
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              fontSize: "0.72rem",
                            }}
                          >
                            {edificio.edificio}
                          </span>
                          {completo && (
                            <FaCheckCircle className="text-success" style={{ fontSize: "0.55rem", flexShrink: 0 }} />
                          )}
                        </div>
                        {edificio.direccion && (
                          <div
                            className="text-muted"
                            style={{
                              fontSize: "0.6rem",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {edificio.direccion}
                          </div>
                        )}
                      </div>

                      {/* Foto */}
                      {foto && (
                        <div style={{ width: "100%", height: itemHeight, position: "relative" }}>
                          <img
                            src={foto.url}
                            alt=""
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              cursor: "pointer",
                              borderRadius: video ? 0 : "0 0 5px 5px",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(foto.url, "_blank");
                            }}
                          />
                          {canEdit && (
                            <button
                              className="btn btn-sm position-absolute"
                              style={deleteBtnStyle}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteMedia(foto);
                              }}
                              title="Eliminar foto"
                            >
                              <FaTrash style={{ fontSize: "0.5rem" }} />
                            </button>
                          )}
                        </div>
                      )}

                      {/* Video */}
                      {video && (
                        <div style={{ width: "100%", height: itemHeight, position: "relative" }}>
                          <video
                            src={video.url}
                            autoPlay
                            muted
                            loop
                            playsInline
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              cursor: "pointer",
                              borderRadius: "0 0 5px 5px",
                              display: "block",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(video.url, "_blank");
                            }}
                          />
                          {canEdit && (
                            <button
                              className="btn btn-sm position-absolute"
                              style={deleteBtnStyle}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteMedia(video);
                              }}
                              title="Eliminar video"
                            >
                              <FaTrash style={{ fontSize: "0.5rem" }} />
                            </button>
                          )}
                        </div>
                      )}

                      {/* Ícono subir — grande si vacío, chico si falta un slot */}
                      {canEdit && tieneSlotLibre && (
                        <div
                          className="text-muted d-flex align-items-center justify-content-center"
                          style={{ height: mediaEdificio.length > 0 ? 24 : 40 }}
                        >
                          <FaUpload
                            style={{
                              fontSize: mediaEdificio.length > 0 ? "0.65rem" : "0.9rem",
                              opacity: 0.3,
                            }}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {canEdit && (
        <SubirFotoReporteModal
          show={showUploadModal}
          onHide={() => {
            setShowUploadModal(false);
            setUploadEdificio(null);
          }}
          onUploadSuccess={() => {
            setShowUploadModal(false);
            setUploadEdificio(null);
            refreshFotos();
          }}
          onDeleteMedia={handleDeleteMedia}
          edificio={uploadEdificio}
          saleId={selectedSaleId ? Number(selectedSaleId) : null}
          mediaActual={(uploadEdificio && fotosPorEdificio[uploadEdificio.id]) || []}
          userId={userId}
          canEdit={canEdit}
        />
      )}
    </div>
  );
};

export default ReporteFotografico;
